import { MAX_GALLERY_PHOTOS } from "@luckymenu/core";
import { useFileUpload } from "./use-file-upload";
import { useRestaurantProfile } from "./use-restaurant-profile";

/** The list with one photo moved by `delta` places; unchanged at either end. */
export function movePhoto(ids: readonly string[], index: number, delta: -1 | 1): string[] {
	const to = index + delta;
	if (to < 0 || to >= ids.length) return [...ids];
	const next = [...ids];
	const [moved] = next.splice(index, 1);
	if (moved) next.splice(to, 0, moved);
	return next;
}

/**
 * More > Restaurant profile > Gallery (P7-T16, WS-08), shared by both apps:
 * the website's gallery photos in order. Adding uploads a `gallery` photo and
 * appends it; removing and moving save the new list. The site shows the
 * change within seconds (the profile update drops the storefront's copy).
 * Needs `settings.manage`.
 */
export function useGallery() {
	const profile = useRestaurantProfile();
	const upload = useFileUpload("gallery");
	const ids = profile.restaurant?.galleryFileIds ?? [];
	const save = (galleryFileIds: string[]) => profile.save({ galleryFileIds });
	return {
		photos: ids,
		max: MAX_GALLERY_PHOTOS,
		full: ids.length >= MAX_GALLERY_PHOTOS,
		busy: upload.state.status === "uploading" || profile.saving,
		upload: upload.state,
		saveError: profile.saveError,
		/** Picks a photo, uploads it and adds it at the end. */
		async add(): Promise<void> {
			if (ids.length >= MAX_GALLERY_PHOTOS) return;
			const info = await upload.pickAndUpload();
			if (info) await save([...ids, info.id]);
		},
		remove: (fileId: string) => save(ids.filter((id) => id !== fileId)),
		move: (index: number, delta: -1 | 1) => save(movePhoto(ids, index, delta)),
	};
}
